<template>
    <section class="panel panel--content panel--grid-content panel--grid-filter panel--spaced panel--site-listing" :class="'panel--' + panel_options.panel_background_colour">
        <div class="panel__body">

            <aside class="content content--aside content--aside-filter content--aside-top content--aside-sticky filter filter--vertical">
                <div class="filter__inside">
                    <div class="alert alert--primary-tint">
                        <div v-if="panel_options.toggle_search_field == true" class="filter__input">
                            <div class="filter__box">
                                <input v-model="search" v-debounce:500ms="filter_sites" id="site-search" class="form-control" type="text" placeholder="Filter sites by keyword" aria-label="sites filter - search for a site by name and tab through the matching sites in the section below" autocomplete="off">
                            </div>
                        </div>
                        <button @click="filter_visibility = !filter_visibility" type="button" id="filter__toggle" aria-controls="secondary-filters" class="filter__toggle" aria-expanded="false">
                            <span tabindex="-1">Filters</span>
                        </button>
                        <div id="secondary-filters" class="filter__secondary" :class="{ 'is-visible': filter_visibility }">
                            <h3 class="alert__title">Filter by</h3>
                            <div class="filter__categories filter__section" id="filter__categories--ug">
                                <div v-if="panel_options.toggle_categories_dropdown == true" class="filter__select">
                                    <select v-model="selected_category" class="custom-select form-control" aria-label="academic divisions">
                                        <option v-for="division in divisional_categories" :key="division.value" :value="division.value">
                                            {{ division.name }}
                                        </option>
                                    </select>
                                </div>
                                <div v-if="panel_options.toggle_signature_themes_dropdown == true" class="filter__select">
                                    <select v-model="selected_signature_theme" class="custom-select form-control" aria-label="signature research themes">
                                        <option v-for="signature_theme in signature_theme_categories" :key="signature_theme.value" :value="signature_theme.value">
                                            {{ signature_theme.name }}
                                        </option>
                                    </select>
                                </div>
                                <div v-if="panel_options.toggle_research_strengths_dropdown == true" class="filter__select">
                                    <select v-model="selected_research_strength" class="custom-select form-control" aria-label="research strengths">
                                        <option v-for="research_strength in research_strength_categories" :key="research_strength.value" :value="research_strength.value">
                                            {{ research_strength.name }}
                                        </option>
                                    </select>
                                </div>
                                <label class="filter__checkbox" v-if="panel_options.toggle_current_projects_checkbox">
                                    Current projects
                                    <input type="checkbox" name="current projects" v-model="show_only_current_projects" @change="filter_sites();" id="current-projects-checkbox" aria-label="current projects" class="form-control">
                                    <span aria-hidden="true" class="checkmark"></span>
                                </label>
                                <button v-if="show_reset_button" class="text__link filter__reset" style="display:block;" @click="reset_filters" >Reset filters</button>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
            <div class="content content--main content--filter">
                <div class="content--card">
                    <ul class="content content--card-grid">
                         <template v-for="(site, index) in filtered_sites">
                            <card 
                                :key="index"
                                :url="site.url"
                                :image="site.image"
                                :show_current_projects_label="panel_options.card_current_projects_label"
                                :center_align_card_content="panel_options.card_center_align_content"
                                :hide_card_arrows="panel_options.card_hide_arrows"
                                :title="site.title"
                                :content="site.content"
                                :categories="site.categories"
                                :show_categories="panel_options.card_display_categories"
                                :categories_array="categories_array"
                                :categories_string="site.categories_string">
                            </card>
                        </template>
                    </ul>
                    <div v-if="filtered_sites.length == 0" class="alert alert--striped">
						We don't have any research sites matching those filters.
					</div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import Card from './card.vue'
import mixins from '../mixins.js'

export default {
    components: {
        'card': Card
    },
    props: {
        sites_array: Array,
        categories_array: Array,
        prefilters: Array,
        panel_options: Array
    },
    mixins: [ mixins ],
    data() {
        return {
            filter_visibility: false,
            // Filter values
            search: '',
            selected_category: null,
            selected_signature_theme: null,
            selected_research_strength: null,
            // Extracted category arrays
            divisional_categories: [],
            signature_theme_categories: [],
            research_strength_categories: [],
            // Filtered sites
            filtered_sites: [],
            show_only_current_projects: false
        }
    },
    computed: {
        show_reset_button() {
            if(this.search.length > 0 || this.selected_category != 'All Divisions' || this.selected_signature_theme != 'All Signature Research Themes' || this.selected_research_strength != 'All Research Strengths' || this.show_only_current_projects == true) {
                return true;
            }
            return false;
        }
    },    
    watch: {
        selected_category() {
            if(this.selected_category.length > 0) {
                this.filter_sites();
            }
        },
        selected_signature_theme() {
            if(this.selected_signature_theme.length > 0) {
                if(this.prefilters.top_tier == 'Signature Research Themes') {
                    this.selected_category = this.selected_signature_theme;
                    return true;
                }
                this.filter_sites();
            }
        },
        selected_research_strength() {
            if(this.selected_research_strength.length > 0) {
                if(this.prefilters.top_tier == 'Research Strengths') {
                    this.selected_category = this.selected_research_strength;
                    return true;
                }
                this.filter_sites();
            }
        }
    },
    methods: {
        extract_divisional_categories() {
            // Grab the Divisions from the categories array and copy them into another for the dropdown filter
            this.divisional_categories = [];

            if(this.prefilters.top_tier == 'divisions' && this.selected_category == null || this.selected_category == 'All Divisions') {
                // Show a list of everything in the 'Divisions' array of sites

                // First object
                let choose_obj = {
                    name: 'Choose a Division',
                    value: 'All Divisions'
                }
                this.divisional_categories.push(choose_obj);

                for(let i = 0; i < this.categories_array.length; i++) {
                    if(this.categories_array[i].top_tier == 'Divisions') {
                        // If the top tier preselect is divisions then 
                        this.divisional_categories.push(this.categories_array[i]);
                    }              
                }
            } else {
                // Since its a subdivision lets only load those into the dropdown with a matching top tier or second tier category
                for(let i = 0; i < this.categories_array.length; i++) {
                    if(this.categories_array[i].parent && this.categories_array[i].value) {
                        if(this.categories_array[i].parent == this.prefilters.top_tier || this.categories_array[i].value == this.prefilters.top_tier || this.categories_array[i].parent == this.prefilters.second_tier || this.categories_array[i].value == this.prefilters.second_tier) {
                            this.divisional_categories.push(this.categories_array[i]);
                        }
                    }
                }
            }
        },
        extract_signature_theme_categories() {
            // Grab the Signature Research Themes from the categories array and copy them into another for the dropdown filter
            this.signature_theme_categories = [];

            // First object
            let choose_obj = {
                name: 'Choose a Signature Research Theme',
                value: 'All Signature Research Themes'
            }
            this.signature_theme_categories.push(choose_obj);
            
            for(let i = 0; i < this.categories_array.length; i++) {
                if(this.categories_array[i].top_tier == 'Signature Research Themes') {
                    this.signature_theme_categories.push(this.categories_array[i]);
                }
            }

            // If we have signature themes set as the top tier category then copy over the Signature Research Themes to the dropdown
            if(this.prefilters.top_tier == 'Signature Research Themes') {
                this.divisional_categories = this.signature_theme_categories;
            }
        },
        extract_research_strength_categories() {
            // Grab the Research Strengths from the categories array and copy them into another for the dropdown filter
            this.research_strength_categories = [];

            // First object
            let choose_obj = {
                name: 'Choose a Research Strength',
                value: 'All Research Strengths'
            }
            this.research_strength_categories.push(choose_obj);
            
            for(let i = 0; i < this.categories_array.length; i++) {
                if(this.categories_array[i].top_tier == 'Research Strengths') {
                    this.research_strength_categories.push(this.categories_array[i]);
                }
            }

            // If we have researcvh strengths set as the top tier category then copy over the Research Strengths to the dropdown
            if(this.prefilters.top_tier == 'Research Strengths') {
                this.divisional_categories = this.research_strength_categories;
            }
        },
        filter_sites() {
            let sites = JSON.parse(JSON.stringify(this.sites_array)); // Make an immutable copy, this is because of Vues reactive nature it damages the original otherwise
            let prefilters = this.prefilters;
            let search = this.search;
            let filtered_sites = this.filtered_sites;
            let top_tier = this.capitalise_string(prefilters.top_tier);
            let selected_category = this.selected_category;
            let selected_signature_theme = this.selected_signature_theme;
            let selected_research_strength = this.selected_research_strength;
        
            // Lets reset our array of filtered sites
            this.filtered_sites = [];

            // Define our selected categories

            // Ensure that a Division category is set, this is important for the dropdowns
            if(this.selected_category == undefined || this.selected_category == null || this.selected_category == false) {
                this.selected_category = 'All Divisions';
            }

            // Ensure that a Signature Research Theme category is set, this is important for the dropdowns
            if(this.selected_signature_theme == undefined || this.selected_signature_theme == null || this.selected_signature_theme == false) {
                if(prefilters.top_tier == 'Signature Research Themes') {
                    this.selected_signature_theme = this.selected_category;
                } else {
                    this.selected_signature_theme = 'All Signature Research Themes';
                }
            }

            // Ensure that a Research Strenth category is set, this is important for the dropdowns
            if(this.selected_research_strength == undefined || this.selected_research_strength == null || this.selected_research_strength == false) {
                if(prefilters.top_tier == 'Research Strengths') {
                    this.selected_research_strength = this.selected_category;
                } else {
                    this.selected_research_strength = 'All Research Strengths';
                }
            }

            // FILTERS

            // First filter by the search string
            if(search.length > 0) {
                // Search string is present, so loop through and filter
                this.filtered_sites = sites.filter(function(site) {
                    // For each item in site object
                    for(var key in site) {
                        let value = site[key];
                        if(site[key] !== null) {
                            // Need to loop through the keys rather than specifying them
                            if(typeof value === 'string') {
                                if(value.toUpperCase().indexOf(search.toUpperCase()) !== -1) {
                                    if(!filtered_sites.includes(site)) {
                                        return true
                                    }
                                }
                            }
                            // Support for the categories and keywords arrays
                            if(typeof value === 'object') {
                                for(var j = 0; j < value.length; j++) {
                                    if(value[j].toUpperCase().indexOf(search.toUpperCase()) !== -1) {
                                        if(!filtered_sites.includes(site)) {
                                            return true
                                        }
                                    } 
                                }
                            }
                        }
                    }
                }); 
            } else {
                // No search value so use ALL sites
                this.filtered_sites = sites;
            }
            
            // Filter against current projects if checkbox is selected
            if(this.show_only_current_projects == true) {
                this.filtered_sites = this.filtered_sites.filter(function(site) {
                    return site.categories.includes('Current project')
                })
            } else {
                this.filtered_sites = this.filtered_sites
            }
            
            // Filter against the First tier (Divisions, Impact, etc)
            this.filtered_sites = this.filtered_sites.filter(function(site) {
                return site.categories.includes(top_tier)
            })
            
            // Filter against the Second tier (Sub divisions, signature research themes)
            if(this.selected_category == 'All Divisions' || this.selected_category == 'All Signature Research Themes' || this.selected_category == 'All Research Strengths') {
                // Do nothing
            } else {   
                this.filtered_sites = this.filtered_sites.filter(function(site) {
                    return site.categories.includes(selected_category)
                })
            }

            // Filter against the Signature theme
            if(this.selected_signature_theme == 'All Signature Research Themes' || this.selected_signature_theme == null) {
                // Do nothing
            } else {   
                this.filtered_sites = this.filtered_sites.filter(function(site) {
                    return site.categories.includes(selected_signature_theme)
                })
            }
            
            // Finally, filter against the Research Strength
            if(this.selected_research_strength == 'All Research Strengths' || this.selected_research_strength == null) {
                // Do nothing
            } else {   
                this.filtered_sites = this.filtered_sites.filter(function(site) {
                    return site.categories.includes(selected_research_strength)
                })
            }
        },
        reset_filters() {
                        
            // If the search field is visible then reset
            if(this.panel_options.toggle_search_field == true) {
                this.search = '';
            }
            
            // If the main category dropdown is visible then reset
            if(this.panel_options.toggle_categories_dropdown == true) {
                if(this.prefilters.second_tier) {
                    this.selected_category = this.capitalise_string(this.prefilters.second_tier);
                } else {
                    this.selected_category = null;
                }
            }
            
            // If the signature research themes dropdown is visible then reset
            if(this.panel_options.toggle_signature_themes_dropdown == true) {
                if(this.prefilters.signature_theme) {
                    this.selected_signature_theme = this.capitalise_string(this.prefilters.signature_theme);
                } else {
                    this.selected_signature_theme = null;
                }
            }
            
            // If the research strength dropdown is visible then reset
            if(this.panel_options.toggle_research_strengths_dropdown == true) {
                if(this.prefilters.research_strength) {
                    this.selected_research_strength = this.capitalise_string(this.prefilters.research_strength);
                } else {
                    this.selected_research_strength = null;
                }
            }

            this.show_only_current_projects = false;

            this.filter_sites();

        }
    },
    created() {
        // Set the category
        this.selected_category = this.capitalise_string(this.prefilters.second_tier); // This is the mutable category used for the dropdown

        // Set the Signature Theme
        this.selected_signature_theme = this.capitalise_string(this.prefilters.signature_theme);
        this.selected_research_strength = this.capitalise_string(this.prefilters.research_strength);

        // Only run if divisions
        if(this.prefilters.top_tier == 'divisions') {
            this.extract_divisional_categories();
        }
        // Else the dropdown is populated here
        this.extract_signature_theme_categories();
        this.extract_research_strength_categories();

        this.filter_sites();
    },
    beforeDestroy() {
        localStorage.removeItem('sitesArray');
    },
}
</script>